import React, { useState } from 'react';
import { MATERIALS, PIPE_DATABASE } from '../engine/pipeDatabase';

export const ReferenceTables: React.FC = () => {
  const [selectedKey, setSelectedKey] = useState<string>('copper_en1057');
  const [search, setSearch] = useState<string>('');

  const currentMaterial = MATERIALS[selectedKey] || MATERIALS.copper_en1057;
  const pipes = (PIPE_DATABASE[selectedKey] || []).filter((p) =>
    p.nominalSize.toLowerCase().includes(search.toLowerCase()) ||
    p.innerDiameterMm.toString().includes(search)
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
      <div>
        <span className="text-xs font-bold uppercase tracking-wider text-[#087F9C] block mb-1">
          Verified Technical Datasets
        </span>
        <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#10283D]">
          Pipe Dimensions & Reference Tables
        </h1>
        <p className="text-slate-600 text-sm mt-1 max-w-3xl">
          Verified outside diameters, wall thicknesses, and internal bores from BS EN 1057, ASTM B88, ASME B36.10M, ASTM D1785, and BS 8588.
        </p>
      </div>

      {/* Material Selector Buttons */}
      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-3">
        {Object.values(MATERIALS).map((m) => (
          <button
            key={m.id}
            onClick={() => setSelectedKey(m.id)}
            className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${
              selectedKey === m.id
                ? 'bg-[#10283D] text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            {m.name.split(' (')[0]}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h3 className="font-display font-bold text-lg text-[#10283D]">{currentMaterial.name}</h3>
            <p className="text-xs text-slate-500">{currentMaterial.description} (Roughness ε = {currentMaterial.roughnessMm} mm)</p>
          </div>
          <input
            type="text"
            placeholder="Search size..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 rounded-xl border border-slate-300 text-xs outline-none focus:border-[#087F9C]"
          />
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-slate-50 text-slate-700 font-bold uppercase border-y border-slate-200">
              <tr>
                <th className="p-3">Nominal Size</th>
                <th className="p-3">Outside Dia (OD)</th>
                <th className="p-3">Wall Thickness</th>
                <th className="p-3">Internal Dia (ID)</th>
                <th className="p-3">Flow @ 1.0 m/s</th>
                <th className="p-3">Flow @ 1.5 m/s</th>
                <th className="p-3">Flow @ 2.0 m/s</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-mono">
              {pipes.map((p) => {
                const area = (Math.PI * Math.pow(p.innerDiameterMm / 1000, 2)) / 4;
                return (
                  <tr key={p.nominalSize} className="hover:bg-slate-50">
                    <td className="p-3 font-bold text-slate-900 font-sans">{p.nominalSize}</td>
                    <td className="p-3">{p.outerDiameterMm.toFixed(2)} mm</td>
                    <td className="p-3">{p.wallThicknessMm.toFixed(2)} mm</td>
                    <td className="p-3 font-bold text-teal-700">{p.innerDiameterMm.toFixed(2)} mm</td>
                    <td className="p-3">{(1.0 * area * 1000).toFixed(2)} L/s</td>
                    <td className="p-3 font-semibold text-emerald-700">{(1.5 * area * 1000).toFixed(2)} L/s</td>
                    <td className="p-3 text-slate-600">{(2.0 * area * 1000).toFixed(2)} L/s</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
